import { DateTime } from 'luxon';

import {
  IMarketingRange,
  isMarketingIsoDay,
  marketingAutoGranularity,
  marketingPeriodLabel,
  marketingPreviousRange,
  marketingRangeDays,
  marketingRangeIssue,
  marketingRangeLabel,
  MARKETING_DAY_GRANULARITY_MAX_DAYS,
  MarketingMetricKey,
  MarketingPeriodPreset,
  MarketingTabId,
  MARKETING_METRICS,
} from './marketingModel';
import {
  IMarketingCompareInfo,
  IMarketingMetrics,
  IMarketingSeriesPoint,
  MarketingDate,
  MarketingGranularityCode,
} from './types';

/**
 * ТЗ 0103 — сравнение периодов и шаг ряда (design-erp §2.2). Модуль чистый:
 * ни React, ни Apollo, ни i18n — подписи ключами, даты строками ISO.
 *
 * Окно сравнения считает СЕРВЕР (этап 2, `compare_window`): экран берёт
 * даты из `compareInfo` ответа и из дат точек ряда (`previousDateFrom/To`).
 * Здесь — только ОЦЕНКА окна для подписи панели фильтров, пока ответ ещё
 * едет:
 *  - «Прошлый период» — у календарных пресетов (эта/прошлая неделя, месяц,
 *    квартал, год) те же дни прошлой единицы: 1–8 окт ↔ 1–8 сен, сентябрь ↔
 *    август; у скользящих (7/30/90 дней, свои даты) — окно той же длины
 *    прямо перед;
 *  - «Год назад» — правило сервера (повторное ревью 0103): по ЕДИНИЦЕ
 *    пресета, а не по шагу ряда — месяц / квартал / год — те же даты год
 *    назад; неделя, 7/30/90 дней и свои даты — сдвиг на 364 дня (те же дни
 *    недели); одно окно на всех экранах;
 *  - «Свои даты» — как задал человек (≤ 400 дней, порядок дат).
 *
 * Графики выравниваются по НОМЕРУ корзины (день i против дня i), никогда по
 * дате на общей оси; разное число корзин — `null`, то есть разрыв.
 */

/* ── Режимы сравнения ────────────────────────────────────────────────── */

/** Значения параметра `cmp` адреса. */
export const MARKETING_COMPARE_MODES = [
  'prev',
  'none',
  'yoy',
  'custom',
] as const;

export type MarketingCompareMode = typeof MARKETING_COMPARE_MODES[number];

/** Сравнение включено везде по умолчанию (просьба владельца). */
export const MARKETING_DEFAULT_COMPARE: MarketingCompareMode = 'prev';

/** Порядок сегментов «Сравнить» на экране. */
export const MARKETING_COMPARE_UI_ORDER: MarketingCompareMode[] = [
  'none',
  'prev',
  'yoy',
  'custom',
];

export const isMarketingCompareMode = (
  value: string | null | undefined,
): value is MarketingCompareMode =>
  Boolean(value) &&
  (MARKETING_COMPARE_MODES as readonly string[]).includes(value as string);

/** Календарные пресеты: сравниваются с теми же днями прошлой единицы. */
export const MARKETING_CALENDAR_PRESETS: MarketingPeriodPreset[] = [
  'thisWeek',
  'prevWeek',
  'thisMonth',
  'prevMonth',
  'quarter',
  'year',
];

export const marketingIsCalendarPreset = (
  preset: MarketingPeriodPreset,
): boolean => MARKETING_CALENDAR_PRESETS.includes(preset);

/** Единица «прошлого периода» (`MarketingCompareUnit` сервера). */
export const MARKETING_COMPARE_UNITS = [
  'DAY',
  'WEEK',
  'MONTH',
  'QUARTER',
  'YEAR',
  'ROLLING',
] as const;

export type MarketingCompareUnitCode = typeof MARKETING_COMPARE_UNITS[number];

export const isMarketingCompareUnit = (
  value: string | null | undefined,
): value is MarketingCompareUnitCode =>
  Boolean(value) &&
  (MARKETING_COMPARE_UNITS as readonly string[]).includes(value as string);

/**
 * Ревью 0103 (L1): «прошлый период» — единицей по пресету, явно; форму
 * дат сервер больше не угадывает. Скользящие окна (7/30/90 дней, свои
 * даты) — окно той же длины прямо перед (`ROLLING`), календарные — те же
 * дни прошлой недели / месяца / квартала / года.
 */
export const MARKETING_COMPARE_UNIT_BY_PRESET: Readonly<
  Record<MarketingPeriodPreset, MarketingCompareUnitCode>
> = {
  '7d': 'ROLLING',
  '30d': 'ROLLING',
  '90d': 'ROLLING',
  custom: 'ROLLING',
  thisWeek: 'WEEK',
  prevWeek: 'WEEK',
  thisMonth: 'MONTH',
  prevMonth: 'MONTH',
  quarter: 'QUARTER',
  year: 'YEAR',
};

/**
 * Подпись единицы сравнения: `compareInfo.unit` сервера (`month`) или
 * единица запроса (`MONTH`) → ключ `marketing.compare.units.<код>`;
 * незнакомая или нет — `null` (подписи нет).
 */
export const marketingCompareUnitKey = (
  unit: string | null | undefined,
): string | null => {
  const code = (unit ?? '').toUpperCase();
  return isMarketingCompareUnit(code)
    ? `marketing.compare.units.${code.toLowerCase()}`
    : null;
};

/** Единица «прошлого периода» пресета; пресета нет — скользящее окно. */
export const marketingCompareUnit = (
  preset: MarketingPeriodPreset | null | undefined,
): MarketingCompareUnitCode =>
  preset ? MARKETING_COMPARE_UNIT_BY_PRESET[preset] : 'ROLLING';

/* ── Шаг ряда ────────────────────────────────────────────────────────── */

/** Значения параметра `step` адреса. */
export const MARKETING_STEPS = ['day', 'week', 'month'] as const;

export type MarketingStep = typeof MARKETING_STEPS[number];

export const isMarketingStep = (
  value: string | null | undefined,
): value is MarketingStep =>
  Boolean(value) &&
  (MARKETING_STEPS as readonly string[]).includes(value as string);

export const MARKETING_STEP_GRANULARITY: Readonly<
  Record<MarketingStep, MarketingGranularityCode>
> = {
  day: 'DAY',
  week: 'WEEK',
  month: 'MONTH',
};

/** «День» недоступен на окне длиннее 92 дней (контракт сервера). */
export const marketingStepAllowed = (
  step: MarketingStep,
  range: IMarketingRange,
): boolean =>
  step !== 'day' ||
  marketingRangeDays(range) <= MARKETING_DAY_GRANULARITY_MAX_DAYS;

/**
 * Шаг ряда для запроса: не выбран — автоматически по длине окна; «день» на
 * окне длиннее 92 дней — неделя (подсказка «больше 92 дней — по неделям»).
 */
export const marketingResolveStep = (
  step: MarketingStep | null | undefined,
  range: IMarketingRange,
): MarketingGranularityCode => {
  if (!step) return marketingAutoGranularity(range);
  if (!marketingStepAllowed(step, range)) return 'WEEK';
  return MARKETING_STEP_GRANULARITY[step];
};

/* ── Диапазон сравнения ──────────────────────────────────────────────── */

const day = (iso: string): DateTime => DateTime.fromISO(iso, { zone: 'utc' });
const isoOf = (value: DateTime): MarketingDate => value.toISODate();

const isMonthEnd = (value: DateTime): boolean =>
  value.day === value.endOf('month').day;

/**
 * Сдвиг дня на N месяцев: последний день месяца остаётся последним (30.09 →
 * 31.08, 29.02 → 31.01), прочие — тем же числом с упором в конец месяца
 * (31.03 − 1 мес. → 28.02/29.02).
 */
const shiftMonths = (value: DateTime, months: number): DateTime => {
  const shifted = value.minus({ months });
  return isMonthEnd(value) ? shifted.endOf('month').startOf('day') : shifted;
};

/** Окно из целых ISO-недель: с понедельника, длиной кратной 7. */
export const marketingIsWeekWindow = (
  range: IMarketingRange,
  preset?: MarketingPeriodPreset,
): boolean => {
  if (preset === 'thisWeek' || preset === 'prevWeek') return true;
  const from = day(range.from);
  return from.weekday === 1 && marketingRangeDays(range) % 7 === 0;
};

export interface IMarketingCompareRequest {
  preset: MarketingPeriodPreset;
  range: IMarketingRange;
  mode: MarketingCompareMode;
  /** Свои даты сравнения (`cfrom`/`cto`) — только при `custom`. */
  custom?: Partial<IMarketingRange> | null;
}

/** Единицы, у которых «год назад» — те же даты (иначе −364 дня). */
const SAME_DATES_YEAR_UNITS: ReadonlyArray<MarketingCompareUnitCode> = [
  'MONTH',
  'QUARTER',
  'YEAR',
];

/** «Прошлый период» календарного пресета — те же дни прошлой единицы. */
const previousUnit = (
  preset: MarketingPeriodPreset,
  range: IMarketingRange,
): IMarketingRange => {
  const from = day(range.from);
  const to = day(range.to);
  switch (preset) {
    case 'thisWeek':
    case 'prevWeek':
      return {
        from: isoOf(from.minus({ days: 7 })),
        to: isoOf(to.minus({ days: 7 })),
      };
    case 'quarter':
      return {
        from: isoOf(shiftMonths(from, 3)),
        to: isoOf(shiftMonths(to, 3)),
      };
    case 'year':
      return {
        from: isoOf(from.minus({ years: 1 })),
        to: isoOf(to.minus({ years: 1 })),
      };
    case 'thisMonth':
    case 'prevMonth':
    default:
      return {
        from: isoOf(shiftMonths(from, 1)),
        to: isoOf(shiftMonths(to, 1)),
      };
  }
};

/**
 * «Год назад» — правило сервера (`compare_window`, повторное ревью 0103):
 * единица пресета месяц / квартал / год — те же даты год назад (конец
 * месяца — в конец месяца, 29.02 → 28.02); неделя, скользящие 7/30/90 дней
 * и свои даты — −364 дня (те же дни недели).
 */
const yearAgo = (
  range: IMarketingRange,
  preset?: MarketingPeriodPreset,
): IMarketingRange => {
  const byDays = !SAME_DATES_YEAR_UNITS.includes(marketingCompareUnit(preset));
  if (byDays) {
    return {
      from: isoOf(day(range.from).minus({ days: 364 })),
      to: isoOf(day(range.to).minus({ days: 364 })),
    };
  }
  return {
    from: isoOf(shiftMonths(day(range.from), 12)),
    to: isoOf(shiftMonths(day(range.to), 12)),
  };
};

/**
 * Диапазон сравнения; `null` — сравнения нет (выключено или свои даты не
 * заданы / неверны).
 */
export const marketingCompareRange = ({
  preset,
  range,
  mode,
  custom,
}: IMarketingCompareRequest): IMarketingRange | null => {
  switch (mode) {
    case 'none':
      return null;
    case 'custom':
      return custom && marketingRangeIssue(custom) === null
        ? (custom as IMarketingRange)
        : null;
    case 'yoy':
      return yearAgo(range, preset);
    case 'prev':
    default:
      return marketingIsCalendarPreset(preset)
        ? previousUnit(preset, range)
        : marketingPreviousRange(range);
  }
};

/** Подпись диапазона сравнения: «к 01.08–31.08 · 31 дн.» — данными. */
export const marketingCompareSummary = (
  range: IMarketingRange,
  currentYear?: number,
): { label: string; days: number } => ({
  label: marketingRangeLabel(range, currentYear),
  days: marketingRangeDays(range),
});

/* ── Корзины и выравнивание ──────────────────────────────────────────── */

export interface IMarketingBucket {
  /** Номер корзины в окне, с нуля. */
  index: number;
  /** Ключ точки ряда сервера: `2026-09-14`, `2026-W38`, `2026-09`. */
  key: string;
  /** Дни корзины ВНУТРИ окна (крайние корзины обрезаны окном). */
  from: MarketingDate;
  to: MarketingDate;
  days: number;
  /** Корзина собрана не целиком: обрезанная неделя, неполный месяц. */
  partial: boolean;
}

const pad2 = (value: number) => String(value).padStart(2, '0');

const bucketKey = (
  value: DateTime,
  granularity: MarketingGranularityCode,
): string => {
  if (granularity === 'DAY') return isoOf(value);
  if (granularity === 'WEEK') {
    return `${value.weekYear}-W${pad2(value.weekNumber)}`;
  }
  return `${value.year}-${pad2(value.month)}`;
};

const STEP_DURATION = {
  day: { days: 1 },
  week: { weeks: 1 },
  month: { months: 1 },
} as const;

const unitOf = (granularity: MarketingGranularityCode) => {
  if (granularity === 'DAY') return 'day' as const;
  if (granularity === 'WEEK') return 'week' as const;
  return 'month' as const;
};

/**
 * Корзины окна по шагу: день, ISO-неделя, месяц. Крайние недели и месяцы
 * обрезаны окном и помечены `partial`. Окно неверное — пусто.
 */
export const marketingBuckets = (
  range: IMarketingRange,
  granularity: MarketingGranularityCode,
): IMarketingBucket[] => {
  if (marketingRangeIssue(range) !== null) return [];
  const unit = unitOf(granularity);
  const first = day(range.from);
  const last = day(range.to);
  const buckets: IMarketingBucket[] = [];
  let cursor = first.startOf(unit);
  while (cursor <= last) {
    const unitEnd = cursor.endOf(unit).startOf('day');
    const from = cursor < first ? first : cursor;
    const to = unitEnd > last ? last : unitEnd;
    const days = Math.round(to.diff(from, 'days').days) + 1;
    const full = Math.round(unitEnd.diff(cursor, 'days').days) + 1;
    buckets.push({
      index: buckets.length,
      key: bucketKey(cursor, granularity),
      from: isoOf(from),
      to: isoOf(to),
      days,
      partial: days < full,
    });
    cursor = cursor.plus(STEP_DURATION[unit]).startOf(unit);
  }
  return buckets;
};

/**
 * Подпись корзины в подсказке: день — «Пн 14.09», неделя — «14.09–20.09»,
 * месяц — «09.2026» (обрезанный — «01.10–08.10»).
 */
export const marketingBucketTitle = (
  bucket: Pick<IMarketingBucket, 'from' | 'to' | 'partial'>,
  granularity: MarketingGranularityCode,
  lang?: string | null,
  currentYear?: number,
): string => {
  if (granularity === 'DAY') {
    const weekday = day(bucket.from)
      .setLocale(lang || 'ru')
      .toFormat('ccc');
    return `${weekday} ${marketingPeriodLabel(bucket.from, currentYear)}`;
  }
  if (granularity === 'MONTH' && !bucket.partial) {
    return marketingPeriodLabel(bucket.from.slice(0, 7), currentYear);
  }
  return marketingRangeLabel(bucket, currentYear);
};

export interface IMarketingAlignedPoint<T> {
  index: number;
  /** Ключ текущей корзины. */
  key: string;
  bucket: IMarketingBucket;
  /** Корзина сравнения с тем же номером; `null` — её нет. */
  compareBucket: IMarketingBucket | null;
  current: T | null;
  previous: T | null;
  /** Текущая корзина неполная. */
  partial: boolean;
}

/**
 * Ряды текущего окна и окна сравнения — по номеру корзины. Точки берутся по
 * ключу периода сервера; нет точки — `null`. Корзин сравнения меньше —
 * лишние текущие получают `previous: null` (разрыв).
 */
export const marketingAlignSeries = <T extends { period: string }>(
  buckets: ReadonlyArray<IMarketingBucket>,
  current: ReadonlyArray<T> | null | undefined,
  compareBuckets: ReadonlyArray<IMarketingBucket> | null | undefined,
  previous: ReadonlyArray<T> | null | undefined,
): IMarketingAlignedPoint<T>[] => {
  const byKey = (points: ReadonlyArray<T> | null | undefined) =>
    new Map((points ?? []).map((point) => [point.period, point] as const));
  const currentByKey = byKey(current);
  const previousByKey = byKey(previous);
  return buckets.map((bucket, index) => {
    const compareBucket = compareBuckets?.[index] ?? null;
    return {
      index,
      key: bucket.key,
      bucket,
      compareBucket,
      current: currentByKey.get(bucket.key) ?? null,
      previous: compareBucket
        ? previousByKey.get(compareBucket.key) ?? null
        : null,
      partial: bucket.partial,
    };
  });
};

/* ── Сравнение сервера (этап 2) ──────────────────────────────────────── */

/**
 * Режим сравнения адреса → вход запроса (`marketingGqlCompare`): «без
 * сравнения» и свои даты без целого окна — `null` (аргумент не уходит).
 *
 * `prev` уходит с явной единицей по пресету (`MarketingCompare.unit`,
 * ревью 0103 L1): 7/30/90 дней и свои даты — окно той же длины прямо
 * перед, неделя / месяц / квартал / год — те же дни прошлой единицы.
 * `yoy` — тоже с единицей пресета (повторное ревью 0103, A15): от неё
 * сервер решает «те же даты» или «−364 дня».
 */
export const marketingCompareInput = (
  mode: MarketingCompareMode,
  from: string | null | undefined,
  to: string | null | undefined,
  preset: MarketingPeriodPreset | null = null,
): {
  mode: MarketingCompareMode;
  from: string | null;
  to: string | null;
  unit: MarketingCompareUnitCode | null;
} | null => {
  if (mode === 'none') return null;
  if (mode === 'custom') {
    return from && to && marketingRangeIssue({ from, to }) === null
      ? { mode, from, to, unit: null }
      : null;
  }
  return {
    mode,
    from: null,
    to: null,
    unit:
      mode === 'prev' || mode === 'yoy' ? marketingCompareUnit(preset) : null,
  };
};

/** Окно сравнения из ответа сервера; `null` — сравнения нет. */
export const marketingCompareInfoRange = (
  info: IMarketingCompareInfo | null | undefined,
): IMarketingRange | null =>
  info ? { from: info.dateFrom, to: info.dateTo } : null;

/** Дней в полной корзине шага, начинающейся с `from`. */
const fullBucketDays = (
  from: MarketingDate,
  granularity: MarketingGranularityCode,
): number => {
  if (granularity === 'DAY') return 1;
  if (granularity === 'WEEK') return 7;
  return day(from).daysInMonth;
};

/** Корзина из дат сервера; неполная — дней меньше, чем в шаге. */
const serverBucket = (
  index: number,
  key: string,
  from: MarketingDate,
  to: MarketingDate,
  granularity: MarketingGranularityCode,
): IMarketingBucket => {
  const days = Math.round(day(to).diff(day(from), 'days').days) + 1;
  return {
    index,
    key,
    from,
    to,
    days,
    partial: days < fullBucketDays(from, granularity),
  };
};

/**
 * Точки ряда СЕРВЕРА со сравнением (`compare`) → выравнивание по номеру
 * корзины: даты корзин и корзин сравнения — из ответа (`dateFrom/To`,
 * `previousDateFrom/To`), экран окно не считает. Точка без дат (ответ без
 * них) — корзина из `fallback` того же номера; ни того, ни другого — точка
 * пропущена. Корзина без пары (`previousPeriod` null) — `previous: null`,
 * то есть разрыв, а не ноль.
 */
export const marketingServerAligned = (
  points: ReadonlyArray<IMarketingSeriesPoint> | null | undefined,
  granularity: MarketingGranularityCode,
  fallback: ReadonlyArray<IMarketingBucket> = [],
): IMarketingAlignedPoint<IMarketingSeriesPoint>[] => {
  const byKey = new Map((points ?? []).map((point) => [point.period, point]));
  // Ось — корзины окна по шагу (у сервера те же ключи периодов): точки,
  // которых нет в ответе, — разрыв, а не потерянная колонка. Нет корзин —
  // ось по ответу.
  const ordered: Array<{
    key: string;
    point: IMarketingSeriesPoint | null;
  }> = fallback.length
    ? fallback.map((bucket) => ({
        key: bucket.key,
        point: byKey.get(bucket.key) ?? null,
      }))
    : (points ?? []).map((point) => ({ key: point.period, point }));
  return ordered
    .map(({ key, point }, index) => {
      const own = fallback[index];
      const from = point?.dateFrom ?? own?.from ?? null;
      const to = point?.dateTo ?? own?.to ?? null;
      if (!from || !to) return null;
      const bucket = serverBucket(index, key, from, to, granularity);
      const compareBucket =
        point?.previousPeriod && point.previousDateFrom && point.previousDateTo
          ? serverBucket(
              index,
              point.previousPeriod,
              point.previousDateFrom,
              point.previousDateTo,
              granularity,
            )
          : null;
      const previous: IMarketingSeriesPoint | null =
        compareBucket && point?.previous
          ? {
              period: compareBucket.key,
              dateFrom: compareBucket.from,
              dateTo: compareBucket.to,
              metrics: point.previous,
            }
          : null;
      return {
        index,
        key,
        bucket,
        compareBucket,
        current: point,
        previous,
        partial: bucket.partial,
      };
    })
    .filter(
      (item): item is IMarketingAlignedPoint<IMarketingSeriesPoint> =>
        item !== null,
    );
};

/**
 * Показатели окна сравнения для экрана: только когда сервер посчитал окно
 * (`compareInfo`). Без него `previous` обзора — «окно той же длины перед»
 * по умолчанию сервера, а не выбранное сравнение: дельты к нему врали бы.
 */
export const marketingComparedPrevious = (
  previous: IMarketingMetrics | null | undefined,
  info: IMarketingCompareInfo | null | undefined,
): IMarketingMetrics | null => (info && previous ? previous : null);

/* ── Малая база ──────────────────────────────────────────────────────── */

/**
 * Малая база: меньше 10 событий с обеих сторон — дельта серая «мало
 * данных». Это и есть слой «статистики» без фальшивых тестов значимости.
 */
export const MARKETING_SMALL_BASE = 10;

export type MarketingDeltaConfidence = 'ok' | 'smallBase' | 'none';

export const marketingDeltaConfidence = (
  current: number | null | undefined,
  previous: number | null | undefined,
  min: number = MARKETING_SMALL_BASE,
): MarketingDeltaConfidence => {
  if (
    current === null ||
    current === undefined ||
    previous === null ||
    previous === undefined
  ) {
    return 'none';
  }
  return Math.max(Math.abs(current), Math.abs(previous)) < min
    ? 'smallBase'
    : 'ok';
};

/**
 * Малая база — только у счётчиков (лиды, продажи, договоры): у денег и
 * долей «10 событий» не о чем.
 */
export const marketingMetricConfidence = (
  key: MarketingMetricKey,
  current: number | null | undefined,
  previous: number | null | undefined,
): MarketingDeltaConfidence => {
  const { format } = MARKETING_METRICS[key];
  const confidence = marketingDeltaConfidence(current, previous);
  if (confidence === 'smallBase' && format !== 'count' && format !== 'sales') {
    return 'ok';
  }
  return confidence;
};

/* ── Пресеты «неделя к неделе / месяц к месяцу» ──────────────────────── */

/** Пресеты карточки «периоды»: период и шаг одним кликом. */
export const MARKETING_PERIOD_MATRIX_PRESETS = [
  'weeks8',
  'weeks13',
  'months6',
  'months13',
] as const;

export type MarketingPeriodMatrixPreset = typeof MARKETING_PERIOD_MATRIX_PRESETS[number];

/**
 * Окно и шаг пресета: N недель — с понедельника недели N−1 назад по
 * сегодня; N месяцев — с первого числа месяца N−1 назад по сегодня.
 * 13 месяцев укладываются в лимит окна сервера (≤ 400 дней).
 */
export const marketingPeriodMatrixPreset = (
  preset: MarketingPeriodMatrixPreset,
  today: MarketingDate,
): { range: IMarketingRange; step: MarketingStep } => {
  const end = day(today);
  const weeks = (n: number) => ({
    range: {
      from: isoOf(end.startOf('week').minus({ weeks: n - 1 })),
      to: isoOf(end),
    },
    step: 'week' as const,
  });
  const months = (n: number) => ({
    range: {
      from: isoOf(end.startOf('month').minus({ months: n - 1 })),
      to: isoOf(end),
    },
    step: 'month' as const,
  });
  switch (preset) {
    case 'weeks8':
      return weeks(8);
    case 'weeks13':
      return weeks(13);
    case 'months6':
      return months(6);
    case 'months13':
    default:
      return months(13);
  }
};

/** Окно помесячного графика «по месяцам»: 13 месяцев по сегодня. */
export const marketingThirteenMonths = (
  today: MarketingDate,
): IMarketingRange => marketingPeriodMatrixPreset('months13', today).range;

/** День ISO из адреса годится для своих дат сравнения. */
export const marketingCompareDayValid = (value: string | null): boolean =>
  isMarketingIsoDay(value);

/* ── Панель фильтров: где «Шаг» и «Сравнить» действуют (A8) ───────────── */

export interface IMarketingFilterBarInput {
  tab: MarketingTabId;
  /** Подвкладки — уже приведённые к значению (первая по умолчанию). */
  leadsSubTab?: string | null;
  journeySubTab?: string | null;
  seoSubTab?: string | null;
  /** Открыта панель справа (у сущностей — ряд по шагу). */
  inspectorOpen?: boolean;
}

export interface IMarketingFilterBarView {
  /** «Шаг» — только где есть ряды по шагу. */
  step: boolean;
  /** «Сравнить» — только где сравнение что-то меняет. */
  compare: boolean;
  /** Режимы «Сравнить» на экране; `null` — все. */
  compareModes: MarketingCompareMode[] | null;
  /** Период действует только на блок GA4 ниже (SEO › AI-ответы). */
  ga4Only: boolean;
}

/**
 * Повторное ревью 0103 (A8): «Шаг» и «Сравнить» — там, где они что-то
 * меняют на экране, а не на всей вкладке:
 *  - «Шаг»: «Лиды» › тренд и периоды, «Курсы» (малые графики), «Путь
 *    клиента» › посетители и страницы, и любая вкладка с открытой панелью
 *    справа (ряд сущности). «Реклама» (таблица, карта, матрицы), когорты,
 *    воронки и письма рядов по шагу не имеют;
 *  - «Сравнить»: на когортах нет; на «Неделе к неделе» — только «без / год
 *    назад» (сравнение с прошлым там — колонка с предыдущей); в SEO —
 *    только «Поисковики»;
 *  - SEO › AI-ответы: период — только для блока GA4 ниже (подпись).
 */
export const marketingFilterBarView = ({
  tab,
  leadsSubTab = null,
  journeySubTab = null,
  seoSubTab = null,
  inspectorOpen = false,
}: IMarketingFilterBarInput): IMarketingFilterBarView => {
  const view: IMarketingFilterBarView = {
    step: inspectorOpen,
    compare: true,
    compareModes: null,
    ga4Only: false,
  };
  switch (tab) {
    case 'leads':
      if (leadsSubTab === 'cohorts') return { ...view, compare: false };
      if (leadsSubTab === 'periods') {
        return { ...view, step: true, compareModes: ['none', 'yoy'] };
      }
      return { ...view, step: true };
    case 'courses':
      return { ...view, step: true };
    case 'journey':
      return {
        ...view,
        step:
          inspectorOpen ||
          journeySubTab === 'visitors' ||
          journeySubTab === 'pages',
      };
    case 'ads':
      return view;
    case 'seo':
      return {
        ...view,
        step: false,
        compare: seoSubTab === 'engines',
        ga4Only: seoSubTab === 'ai',
      };
    default:
      return { ...view, step: false, compare: false };
  }
};
